import { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import dayjs from 'dayjs'
import Dialog from '@mui/material/Dialog'
import DialogContent from '@mui/material/DialogContent'
import Divider from '@mui/material/Divider'
import DiscountOutlinedIcon from '@mui/icons-material/DiscountOutlined'
import { alpha } from '@mui/material/styles'

import ErrorBox from "@/components/error-box"
import LoadingBox from '@/components/loading-box'
import type { YearlyFeeDiscount } from '@/types'
import { useYearlyFeeDiscount } from '@/hooks/api/use-yearly-fee-discount'
import RemoveDialog from './remove-dialog'
import { DISCOUNT_TYPE_LABEL_MAP, ROUTE_SEGMENTS } from '@/constants/general'
import { convertFromCents, printHumanDateTime, printPrice } from '@/helpers'
import YearlyFeeDiscountActions from './actions'
import FeedbackAlerts from '@/components/feedback-alerts'
import YearlyFeeDiscountForm from '@/components/forms/yearly-fee-discount'
import DetailRow from '@/components/detail-row'

function YearlyFeeCollectionDiscountDetail({
    discountId,
}: {
    discountId: number,
}) {

    const navigate = useNavigate()

    const { year } = useParams()

    const [success, setSuccess] = useState<string | null>(null)
    const [error, setError] = useState<string | null>(null)
    const [edit, setEdit] = useState<boolean>(false)

    // Action dialogs
    const [remove, setRemove] = useState<YearlyFeeDiscount | null>(null)

    const removalSuccessCallback = () => {
        setRemove(null)
        navigate(`/${ROUTE_SEGMENTS.base}/${ROUTE_SEGMENTS.municipalityManagement}/${ROUTE_SEGMENTS.yearlyFeeCollection}/${year}/${ROUTE_SEGMENTS.yearlyFeeCollectionDiscounts}`)
    }
    
    const { data, status, error: queryError } = useYearlyFeeDiscount(discountId)

    if (status === 'pending') {
            return (
                <LoadingBox />
            )
        }

    if (queryError) {
        return <ErrorBox
            error={queryError}
            message={`Nepovedlo se načíst detail slevy ID ${discountId}`}
        />
    }

    return (
        <>
            <FeedbackAlerts
                success={success}
                error={error}
                onClearSuccess={() => setSuccess(null)}
                onClearError={() => setError(null)}
            />
            {edit ?
                <YearlyFeeDiscountForm
                    yearlyFeeDiscount={data}
                    year={data.year}
                    closeCallback={() => setEdit(false)}
                />
                :
                <Box sx={(theme) => ({
                    border: `1px solid ${theme.palette.divider}`,
                    backgroundColor: 'customGrey.boxBackgroundLight',
                    p: 2.5,
                    borderRadius: 2.5,
                    position: 'relative',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1,
                    flexGrow: 1,
                    overflow: 'hidden',
                })}>
                    <Box
                        sx={(theme) => ({
                        position: 'absolute',
                        top: -10,
                        right: -30,
                        color: alpha(theme.palette.text.primary, 0.02),
                        zIndex: 0,
                        pointerEvents: 'none',
                        })}
                    >
                        <DiscountOutlinedIcon sx={{ fontSize: 200 }} />
                    </Box>
                    <Box sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 1,
                        flexGrow: 1,
                    }}>
                        <Typography variant="h4" component="h2" mb={2}>
                            {data.name}
                        </Typography>
                        <DetailRow label="Typ" wider>
                            <Typography variant="body2" component="span">
                                {DISCOUNT_TYPE_LABEL_MAP[data.type]}
                            </Typography>
                        </DetailRow>
                        <DetailRow label="Výše slevy (Kč)" wider>
                            <Typography variant="body2" component="span">
                                {data.set_amount_to_zero ? 'Kompletní osvobození' :printPrice(convertFromCents(data.amount_cents))}
                            </Typography>
                        </DetailRow>
                        {data.age_from &&
                            <DetailRow label="Věk od" wider>
                                <Typography variant="body2" component="span">
                                    {data.age_from}
                                </Typography>
                            </DetailRow>
                        }
                        {data.age_to &&
                            <DetailRow label="Věk do" wider>
                                <Typography variant="body2" component="span">
                                    {data.age_to}
                                </Typography>
                            </DetailRow>
                        }
                        <DetailRow label="Popis" wider>
                            <Typography variant="body2" component="span">
                                {data.description}
                            </Typography>
                        </DetailRow>
                        {(data.household_id || data.household_member_id) &&
                            <>
                                <Divider sx={{
                                    pt: 1,
                                    mb: 1,
                                }}/>
                                {data.household_id &&
                                    <DetailRow label="Přiřazeno domácnosti ID" wider>
                                        <Typography variant="body2" component="span">
                                            {data.household_id}
                                        </Typography>
                                    </DetailRow>
                                }
                                
                                {data.household_member_id &&
                                    <DetailRow label="Přiřazeno členu domácnosti ID" wider>
                                        <Typography variant="body2" component="span">
                                            {data.household_member_id}
                                        </Typography>
                                    </DetailRow>
                                }
                            </>
                        }
                        <Divider sx={{
                            pt: 1,
                            mb: 1,
                        }}/>
                        <DetailRow label="Vytvořeno" wider>
                            <Typography variant="body2" component="span">
                                {printHumanDateTime(dayjs(data.created_at))}
                            </Typography>
                        </DetailRow>
                        <DetailRow label="Aktualizováno" wider>
                            <Typography variant="body2" component="span">
                                {printHumanDateTime(dayjs(data.updated_at))}
                            </Typography>
                        </DetailRow>
                    </Box>
                    <Box sx={{
                        px: 2.5,
                        pt: 2,
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                    }}>
                        <YearlyFeeDiscountActions
                            yearlyFeeDiscount={data}
                            setRemove={setRemove}
                            setError={setError}
                            setEdit={setEdit}
                            largeButton
                        />
                    </Box>
                    {/* <Box sx={{
                        position: 'absolute',
                        top: 2,
                        right: 2,
                        backgroundColor: 'customGrey.boxBackgroundLight',
                        borderRadius: '1rem',
                    }}>
                        <YearlyFeeDiscountActions
                            yearlyFeeDiscount={data}
                            setRemove={setRemove}
                            setError={setError}
                            setEdit={setEdit}
                        />
                    </Box> */}
                </Box>
            }
            <Dialog
                key={'remove-'+remove?.id}
                open={!!remove}
                onClose={() => setRemove(null)}
            >
                <DialogContent>
                    <RemoveDialog
                        yearlyFeeDiscountId={remove?.id}
                        handleClose={() => setRemove(null)}
                        successCallback={() => removalSuccessCallback()}
                    />
                </DialogContent>
            </Dialog>
        </>
    )
}

export default YearlyFeeCollectionDiscountDetail
